import { TrainingFocus } from "@Shared/schemas/progression/progressionSchemas";
import { useEffect, useReducer } from "react";

import { MINIMUM_LOADING_DURATION } from "@Components/game/progress/training-focus/training-focus-feedback.utils";
import {
  initialTrainingFocusTransitionState,
  trainingFocusTransitionReducer,
} from "@Components/game/progress/training-focus/training-focus-transition.reducer";

interface IUseTrainingFocusTransition {
  metric: TrainingFocus["metric"] | null;
  loading: boolean;
  hasLoadedTrainingFocus: boolean;
}

export const useTrainingFocusTransition = ({ metric, loading, hasLoadedTrainingFocus }: IUseTrainingFocusTransition) => {
  const [state, dispatch] = useReducer(trainingFocusTransitionReducer, initialTrainingFocusTransitionState);
  const loadingStartedAt = state.loading?.startedAt;
  const isTransitionActive = state.status === "transitioning";

  useEffect(() => {
    dispatch({ type: "sync", metric, loading, hasLoadedTrainingFocus, now: Date.now() });
  }, [metric, loading, hasLoadedTrainingFocus]);

  useEffect(() => {
    if (loading || loadingStartedAt === undefined) {
      return;
    }

    const remaining = Math.max(MINIMUM_LOADING_DURATION - (Date.now() - loadingStartedAt), 0);
    const timer = setTimeout(() => dispatch({ type: "loadingElapsed", startedAt: loadingStartedAt }), remaining);
    return () => clearTimeout(timer);
  }, [loading, loadingStartedAt]);

  useEffect(() => {
    if (!isTransitionActive) {
      return;
    }

    const frame = requestAnimationFrame(() => dispatch({ type: "enter" }));
    return () => cancelAnimationFrame(frame);
  }, [isTransitionActive]);

  return {
    displayedMetric: state.displayedMetric ?? null,
    previousMetric: state.status === "transitioning" ? state.previousMetric : null,
    isLoading: loading || state.loading !== null,
    isTransitionActive,
    isTransitionEntered: state.status === "transitioning" && state.entered,
    prepareMetricTransition: () => dispatch({ type: "prepare" }),
    completeMetricTransition: () => dispatch({ type: "complete" }),
  };
};
